import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Bell,
  X,
  CheckCheck,
  Sparkles,
  CheckCircle2,
  Paperclip,
  CalendarCheck,
  ExternalLink,
  Calendar,
  Filter,
  Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { parseReleaseNotes } from '../utils/releaseNotes';
import {
  buildNotifications,
  type AppNotification
} from '../utils/notifications';
import {
  getNotificationLastViewed,
  saveNotificationLastViewed
} from '../utils/preferences';
import type { Profile } from '../types';
import { format } from 'date-fns';
import { renderFormattedText } from '../utils/markdownRenderer';

interface NotificationCenterProps {
  onNavigate?: (view: 'dashboard' | 'supervisor', month?: string, userId?: string) => void;
}

function formatNotificationDate(timestamp: string): string {
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return format(d, 'dd.MM.yyyy');
  } catch {
    return timestamp;
  }
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ onNavigate }) => {
  const { user, isSupervisor } = useAuth();
  const { monthlyReports, monthAttachments, fetchProfiles, timeEntryAudits } = useData();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'all'>('new');
  const [filterType, setFilterType] = useState<'all' | 'releases' | 'actions'>('all');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(() => getNotificationLastViewed(user?.id));
  const [sessionLastViewed, setSessionLastViewed] = useState<string | null>(lastViewedAt);

  // Fetch profiles to get user/supervisor names for notifications
  useEffect(() => {
    let cancelled = false;
    fetchProfiles()
      .then(data => {
        if (!cancelled) setProfiles(data);
      })
      .catch(err => console.error('Failed to load profiles for notifications:', err));
    return () => {
      cancelled = true;
    };
  }, [fetchProfiles]);

  // Keep lastViewedAt in sync with user
  useEffect(() => {
    const stored = getNotificationLastViewed(user?.id);
    setLastViewedAt(stored);
    setSessionLastViewed(stored);
  }, [user?.id]);

  const releaseNotes = useMemo(() => parseReleaseNotes(), []);

  // Compute live notifications using the current stored lastViewedAt for badge
  const liveNotifications = useMemo(() => {
    return buildNotifications({
      releaseNotes,
      monthlyReports,
      monthAttachments,
      timeEntryAudits,
      currentUserId: user?.id,
      isSupervisor,
      profiles,
      lastViewedAt
    });
  }, [releaseNotes, monthlyReports, monthAttachments, timeEntryAudits, user?.id, isSupervisor, profiles, lastViewedAt]);

  const unreadCount = useMemo(() => {
    return liveNotifications.filter(n => n.isNew).length;
  }, [liveNotifications]);

  // When drawer is open, freeze the snapshot time so displayed items don't jump around
  const displayedNotifications = useMemo(() => {
    return buildNotifications({
      releaseNotes,
      monthlyReports,
      monthAttachments,
      timeEntryAudits,
      currentUserId: user?.id,
      isSupervisor,
      profiles,
      lastViewedAt: sessionLastViewed
    });
  }, [releaseNotes, monthlyReports, monthAttachments, timeEntryAudits, user?.id, isSupervisor, profiles, sessionLastViewed]);

  const handleOpen = useCallback(() => {
    const current = getNotificationLastViewed(user?.id);
    setSessionLastViewed(current);
    // If no unread, default tab to 'all' so user sees existing notifications
    const unread = liveNotifications.filter(n => n.isNew).length;
    setActiveTab(unread > 0 ? 'new' : 'all');
    setIsOpen(true);
  }, [user?.id, liveNotifications]);

  const handleClose = useCallback(() => {
    const now = new Date().toISOString();
    saveNotificationLastViewed(now, user?.id);
    setLastViewedAt(now);
    setIsOpen(false);
  }, [user?.id]);

  const handleMarkAllAsRead = useCallback(() => {
    const now = new Date().toISOString();
    saveNotificationLastViewed(now, user?.id);
    setLastViewedAt(now);
    setSessionLastViewed(now);
  }, [user?.id]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Filter items based on activeTab and filterType
  const filteredItems = useMemo(() => {
    return displayedNotifications.filter(item => {
      if (activeTab === 'new' && !item.isNew) return false;
      if (filterType === 'releases' && item.type !== 'release') return false;
      if (filterType === 'actions' && item.type === 'release') return false;
      return true;
    });
  }, [displayedNotifications, activeTab, filterType]);

  const handleItemClick = (item: AppNotification) => {
    if (item.linkView && onNavigate) {
      onNavigate(item.linkView, item.linkMonth, item.linkUserId);
      handleClose();
    } else if (item.metadata?.url) {
      window.open(item.metadata.url, '_blank', 'noopener,noreferrer');
    } else if (item.metadata?.fileUrl) {
      window.open(item.metadata.fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const bellAriaLabel = unreadCount > 0 ? `Notifications (${unreadCount} new)` : 'Notifications';

  return (
    <>
      {/* Bell Button */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label={bellAriaLabel}
        title="Notifications"
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-rose-500 rounded-full ring-2 ring-white"
          />
        )}
      </button>

      {/* Flyout Sidebar / Drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="notification-center-title">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            onClick={handleClose}
            data-testid="notification-backdrop"
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col border-l border-slate-200">
              {/* Header */}
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 id="notification-center-title" className="text-base font-bold text-slate-900">
                      Notifications
                    </h2>
                    <p className="text-xs text-slate-500">
                      {unreadCount > 0 ? `${unreadCount} new since last visit` : 'All caught up'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllAsRead}
                      title="Mark all as read"
                      className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                    >
                      <CheckCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleClose}
                    aria-label="Close notifications"
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-md transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* View Tabs & Category Filters */}
              <div className="px-6 pt-3 pb-2 border-b border-slate-100 bg-white space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex p-0.5 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                    <button
                      type="button"
                      data-testid="notification-tab-new"
                      onClick={() => setActiveTab('new')}
                      className={`px-3 py-1 rounded-md transition-all ${
                        activeTab === 'new'
                          ? 'bg-white text-indigo-700 font-bold shadow-xs'
                          : 'hover:text-slate-900'
                      }`}
                    >
                      New {unreadCount > 0 && `(${unreadCount})`}
                    </button>
                    <button
                      type="button"
                      data-testid="notification-tab-all"
                      onClick={() => setActiveTab('all')}
                      className={`px-3 py-1 rounded-md transition-all ${
                        activeTab === 'all'
                          ? 'bg-white text-indigo-700 font-bold shadow-xs'
                          : 'hover:text-slate-900'
                      }`}
                    >
                      All
                    </button>
                  </div>

                  {/* Filter Pills */}
                  <div className="flex items-center gap-1 text-xs">
                    <Filter className="w-3 h-3 text-slate-400 mr-0.5" />
                    {(['all', 'releases', 'actions'] as const).map(ft => (
                      <button
                        key={ft}
                        type="button"
                        onClick={() => setFilterType(ft)}
                        className={`capitalize px-2 py-0.5 rounded-full transition-colors ${
                          filterType === ft
                            ? 'bg-indigo-50 text-indigo-700 font-semibold'
                            : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {ft}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {filteredItems.length === 0 ? (
                  <NotificationEmptyState
                    activeTab={activeTab}
                    onSwitchToAll={() => setActiveTab('all')}
                  />
                ) : (
                  filteredItems.map(item => (
                    <NotificationCard
                      key={item.id}
                      item={item}
                      onClick={() => handleItemClick(item)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

interface NotificationCardProps {
  item: AppNotification;
  onClick: () => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ item, onClick }) => {
  const isClickable = Boolean(
    (item.type === 'release' && item.metadata?.url) ||
    (item.type === 'document_uploaded' && item.metadata?.fileUrl) ||
    item.linkView
  );

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`relative p-3.5 rounded-xl border transition-all text-left ${
        item.isNew
          ? 'bg-indigo-50/40 border-indigo-200/80 shadow-xs'
          : 'bg-white border-slate-200 hover:border-slate-300'
      } ${isClickable ? 'cursor-pointer hover:bg-slate-50/80' : ''}`}
    >
      <div className="flex items-start gap-3">
        <NotificationIcon type={item.type} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold text-sm text-slate-900 truncate">
              {item.title}
            </span>
            {item.isNew && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white rounded-full shrink-0">
                New
              </span>
            )}
          </div>

          {/* Content: for releases render bullet items directly (no duplicate text or nested box); for actions render message */}
          {item.type === 'release' ? (
            <div className="my-2 space-y-1.5 text-xs text-slate-600">
              {item.metadata?.changes && item.metadata.changes.length > 0 ? (
                item.metadata.changes.map((c, idx) => (
                  <div key={idx} className="flex items-start gap-1.5 leading-relaxed">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                    <span className="flex-1">
                      {c.category && c.category !== 'Updates' && c.category !== 'General' && (
                        <span className="font-semibold text-slate-800">{c.category}: </span>
                      )}
                      {renderFormattedText(c.text)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="leading-relaxed">{renderFormattedText(item.message)}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-600 leading-relaxed mb-2">
              {renderFormattedText(item.message)}
            </p>
          )}

          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-slate-400" />
              {formatNotificationDate(item.timestamp)}
            </span>

            {isClickable && (
              <span className="flex items-center gap-1 text-indigo-600 font-medium hover:underline">
                {item.type === 'release' && item.metadata?.url ? (
                  <>
                    Release Notes <ExternalLink className="w-3 h-3" />
                  </>
                ) : item.type === 'document_uploaded' && item.metadata?.fileUrl ? (
                  <>
                    View File <ExternalLink className="w-3 h-3" />
                  </>
                ) : null}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationIcon: React.FC<{ type: AppNotification['type'] }> = ({ type }) => {
  switch (type) {
    case 'release':
      return (
        <div className="p-2 rounded-lg bg-purple-100 text-purple-700 shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
      );
    case 'month_completed':
      return (
        <div className="p-2 rounded-lg bg-blue-100 text-blue-700 shrink-0">
          <CalendarCheck className="w-4 h-4" />
        </div>
      );
    case 'month_approved':
      return (
        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </div>
      );
    case 'document_uploaded':
      return (
        <div className="p-2 rounded-lg bg-amber-100 text-amber-700 shrink-0">
          <Paperclip className="w-4 h-4" />
        </div>
      );
    case 'supervisor_time_edit':
      return (
        <div className="p-2 rounded-lg bg-amber-100 text-amber-700 shrink-0">
          <Clock className="w-4 h-4" />
        </div>
      );
  }
};

const NotificationEmptyState: React.FC<{
  activeTab: 'new' | 'all';
  onSwitchToAll: () => void;
}> = ({ activeTab, onSwitchToAll }) => {
  return (
    <div className="text-center py-12 px-4">
      <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-3">
        <Bell className="w-6 h-6" />
      </div>
      <p className="text-sm font-semibold text-slate-800 mb-1">
        {activeTab === 'new' ? 'No new notifications' : 'No notifications found'}
      </p>
      <p className="text-xs text-slate-500 max-w-xs mx-auto mb-4">
        {activeTab === 'new'
          ? "You're all caught up! You've seen all notifications since your last visit."
          : 'There are currently no notifications to display.'}
      </p>
      {activeTab === 'new' && (
        <button
          type="button"
          onClick={onSwitchToAll}
          className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold underline"
        >
          View all notifications
        </button>
      )}
    </div>
  );
};
